import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  /**
   * Keep the page out of search results. For utility pages whose URL carries
   * something private — e.g. /redeem?code=... — where an indexed URL would
   * both be useless and leak the code into search.
   */
  noindex?: boolean;
}

/**
 * Sets page-level SEO meta tags dynamically.
 * Falls back to index.html defaults when component unmounts.
 */
export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    const defaultTitle =
      "ZUZU Recovery Club | Sauna, Cold Plunge & InBody Scan — Cape Town";
    const defaultDescription =
      "Self-guided recovery club in Century City, Cape Town. Nordic sauna, cold plunge, Normatec compression boots, red light therapy & InBody scans. Book from R100.";
    const baseUrl = "https://zuzuforyou.com";

    const resolvedOgTitle = ogTitle ?? title;
    const resolvedOgDescription = ogDescription ?? description;
    const resolvedCanonical = canonical ? `${baseUrl}${canonical}` : baseUrl;

    // Title
    document.title = title;

    // Standard meta helpers
    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector<HTMLMetaElement>(selector);
      if (el) el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        document.head.appendChild(el);
      }
      el.href = href;
    };

    setMeta('meta[name="description"]', description);
    if (keywords) setMeta('meta[name="keywords"]', keywords);

    // robots: created on demand, since index.html has no such tag by default.
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    // Canonical
    setLink("canonical", resolvedCanonical);

    // Open Graph
    setMeta('meta[property="og:title"]', resolvedOgTitle);
    setMeta('meta[property="og:description"]', resolvedOgDescription);
    setMeta('meta[property="og:url"]', resolvedCanonical);

    // Twitter
    setMeta('meta[name="twitter:title"]', resolvedOgTitle);
    setMeta('meta[name="twitter:description"]', resolvedOgDescription);

    return () => {
      // Restore index.html defaults on unmount
      document.querySelector('meta[name="robots"]')?.remove();
      document.title = defaultTitle;
      setMeta('meta[name="description"]', defaultDescription);
      setLink("canonical", baseUrl + "/");
      setMeta('meta[property="og:title"]', defaultTitle);
      setMeta('meta[property="og:description"]', defaultDescription);
      setMeta('meta[property="og:url"]', baseUrl + "/");
      setMeta('meta[name="twitter:title"]', defaultTitle);
      setMeta('meta[name="twitter:description"]', defaultDescription);
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    noindex,
  ]);
}
